import { authClient } from '~~/layers/base/lib/auth-client'
import { isEcommerceDesignerRoute } from '~~/layers/base/utils/isEcommerceDesignerRoute'

/**
 * Composable for Better Auth in the frontend. Not wired into Magento/ACCS flows.
 * Use in components/pages that need session or auth actions.
 *
 * - useSession(): reactive session (data, isPending, error, refetch)
 * - isSessionPending: true while the session query is in flight
 * - isLoggedIn: session resolved, user present, not anonymous (anonymous plugin = guest)
 * - signIn / signOut: use authClient.signIn.* and authClient.signOut()
 *
 * **Guest session:** initial anonymous sign-in is handled by the client plugin
 * `better-auth-initial-anonymous.client.ts`. After `signOut`, callers must run
 * `signIn.anonymous()` (see `useBetterAuthLogout`) — a one-shot `watch` on session cannot
 * cover SPA logout (TK-9957).
 *
 * **Ecommerce designer:** `isEcommerceDesignerRoute` forces a logged-out view during the TK-13530
 * injunction; remove when purchase flows use Better Auth.
 */
export function useBetterAuth() {
    const route = useRoute()
    const session = authClient.useSession()

    /** TK-13530 — delete when ecommerce designer uses Better Auth. */
    const skipBetterAuthForRoute = computed(() => isEcommerceDesignerRoute(route))

    const isSessionPending = computed(() => {
        if (skipBetterAuthForRoute.value) {
            return false
        }
        return Boolean(session.value?.isPending)
    })

    /**
     * True when the session has finished loading and the user is a non-anonymous account.
     * (Better Auth’s anonymous plugin still creates a `user` row; those sessions are false here.)
     */
    const isLoggedIn = computed(() => {
        if (skipBetterAuthForRoute.value) {
            return false
        }
        const s = session.value
        if (!s || s.isPending) {
            return false
        }
        const user = s.data?.user as { isAnonymous?: boolean } | undefined
        if (!user) {
            return false
        }
        return user.isAnonymous !== true
    })

    return {
        /** Reactive session state; call in setup (e.g. const session = useBetterAuth().useSession()) */
        useSession: authClient.useSession,
        session,
        isSessionPending,
        isLoggedIn,
        /** Auth client for signIn.* and signOut(); use authClient.signIn.social(), authClient.signOut(), etc. */
        authClient,
    }
}
